import React, { ReactElement, ReactNode } from 'react';
import { Link } from 'react-router-dom';

export type ButtonPreset = 'primary' | 'success' | 'warning' | 'danger' | 'grey';
type ButtonPresetProps = Partial<Record<ButtonPreset, boolean | undefined>>;

// The styles to apply to each preset.
const styles: Record<ButtonPreset, string> = {
  primary:  'bg-secondary text-white',
  success:  'bg-success   text-white',
  warning:  'bg-warning   text-black',
  danger:   'bg-danger    text-white',
  grey:     'bg-disabled  text-white',
} as const;

type ButtonProps = 
  ButtonPresetProps & {
  id?: string,
  className?: string,
  onClick?: () => void,
  linkTo?: string,
  children: ReactNode,
  disabled?: boolean,
  type?: 'button' | 'submit' | 'reset',
  'data-locator'?: string,
  fieldProps?: React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>
};

// Button styles are selected by providing the preset name as a prop, e.g. <Button primary> means the styling for
// preset "primary" will be selected. This code selects the preset from provided props.
const getPresetFromProps = (props: ButtonPresetProps): ButtonPreset | undefined => {
  const presets = Object.keys(props).filter(
    key => Object.keys(styles).includes(key) && !!props[key as ButtonPreset],
  ) as ButtonPreset[];
  if (presets.length > 1) {
    console.warn(`Multiple presets provided to Button: ${presets.map(p => `"${p}"`).join(', ')}. Provide only one.`);
  }
  return presets[0];
};

/**
 * A reusable Button. Selecting a preset via `<Button {preset} />` applies standard styling.
 * Not providing a preset will result in an unstyled button, which can be styled with the 'className' prop.
 * Available presets are: 'primary', 'success', 'warning', 'danger', and 'grey'.
 * Providing a linkTo prop will make clicking the button link to the provided path.
 */
export const Button = (props: ButtonProps): ReactElement => {
  // Apply styling only if a preset is defined.
  const preset = getPresetFromProps(props);
  const className = preset
    ? ('text-base font-semibold rounded-md shadow-md p-2 transition ease-out duration-500 transform '
      + 'hover:-translate-y-0.5  hover:scale-110 hover:opacity-80 whitespace-nowrap '
      + `${styles[preset]} ${props.className}`)
    : props.className;

  const fieldProps = {
    className,
    onClick: props.onClick,
    disabled: props.disabled,
    id: props.id,
    type: props.type || 'button',
    'data-locator': props['data-locator'],
    ...props.fieldProps,
  };

  if (props.linkTo && !props.disabled) {
    return <button {...fieldProps}><Link to={props.linkTo}>{props.children}</Link></button>;
  }
  return <button {...fieldProps} id={props.id}>{props.children}</button>;
};

